import { endpointIdToUuid } from "./inventoryRows.js";
import { restPost } from "./restClient.js";

export const MANAGED_CONFIG_VENDOR_CLAUDE_CODE =
    "MANAGED_AGENT_CONFIG_VENDOR_CLAUDE_CODE";

// Used only when a caller names no agent; every page caller passes its label.
const DEFAULT_AGENT_LABEL = "the AI agent";

function first(raw, camel, snake) {
    return raw?.[camel] ?? raw?.[snake];
}

// Release states as the page reads them. The gateway writes enum names; a
// numeric value is accepted too, so a gateway option change cannot blank them.
export const RELEASE_STATE = Object.freeze({
    PILOTING: "piloting",
    RELEASED: "released",
    ABANDONED: "abandoned",
});
const RELEASE_STATE_BY_WIRE = new Map([
    ["MANAGED_AGENT_CONFIG_RELEASE_STATE_PILOTING", RELEASE_STATE.PILOTING],
    ["MANAGED_AGENT_CONFIG_RELEASE_STATE_RELEASED", RELEASE_STATE.RELEASED],
    ["MANAGED_AGENT_CONFIG_RELEASE_STATE_ABANDONED", RELEASE_STATE.ABANDONED],
    [1, RELEASE_STATE.PILOTING],
    [2, RELEASE_STATE.RELEASED],
    [3, RELEASE_STATE.ABANDONED],
]);

function normalizeReleaseState(raw) {
    return RELEASE_STATE_BY_WIRE.get(raw) ?? "";
}
export function normalizeManagedConfigVersion(raw) {
    if (!raw) return null;
    return {
        versionId: first(raw, "versionId", "version_id") ?? "",
        generation: String(raw.generation ?? "0"),
        document: raw.document ?? "",
        documentSha256: first(raw, "documentSha256", "document_sha256") ?? "",
        restoredFromVersionId:
            first(raw, "restoredFromVersionId", "restored_from_version_id") ?? "",
        createdBy: first(raw, "createdBy", "created_by") ?? "",
        createdByEmail: first(raw, "createdByEmail", "created_by_email") ?? "",
        createdAt: first(raw, "createdAt", "created_at") ?? null,
        unmanage: raw.unmanage ?? false,
        releaseId: first(raw, "releaseId", "release_id") ?? "",
        releaseState: normalizeReleaseState(first(raw, "releaseState", "release_state")),
        signedBy: first(raw, "signedBy", "signed_by") ?? "",
        signedByEmail: first(raw, "signedByEmail", "signed_by_email") ?? "",
        signedAt: first(raw, "signedAt", "signed_at") ?? null,
    };
}

export function normalizeManagedConfigRejections(raw) {
    const items = raw?.rejections ?? [];
    return items.map((item) => ({
        pointer: item?.pointer ?? "",
        reason: item?.reason ?? "MANAGED_AGENT_CONFIG_REJECTION_REASON_UNSPECIFIED",
    }));
}

export function normalizeManagedConfigResponse(raw) {
    const currentVersion = first(raw, "currentVersion", "current_version");
    return {
        currentVersion: normalizeManagedConfigVersion(currentVersion),
        enabled: raw?.enabled ?? false,
        updatedAt: first(raw, "updatedAt", "updated_at") ?? null,
    };
}

export async function getManagedAgentConfig(vendor) {
    const raw = await restPost("/v1/managed-agent-config:get", {
        vendor,
    });
    return normalizeManagedConfigResponse(raw);
}

export async function listManagedAgentConfigVersions(vendor, { limit = 50, beforeGeneration = "0" } = {}) {
    const raw = await restPost("/v1/managed-agent-config:list-versions", {
        vendor,
        limit,
        before_generation: String(beforeGeneration),
    });
    return (raw?.versions ?? []).map(normalizeManagedConfigVersion);
}

export async function saveManagedAgentConfig(vendor, { document, expectedGeneration, saveNonce }) {
    const raw = await restPost("/v1/managed-agent-config:save", {
        vendor,
        document,
        expected_generation: String(expectedGeneration),
        save_nonce: saveNonce,
    });
    const rejected = raw?.rejected;
    return {
        version: normalizeManagedConfigVersion(raw?.version),
        rejections: rejected ? normalizeManagedConfigRejections(rejected) : [],
        idempotentReplay: first(raw, "idempotentReplay", "idempotent_replay") ?? false,
    };
}

export async function rollbackManagedAgentConfig(vendor, { versionId, expectedGeneration }) {
    const raw = await restPost("/v1/managed-agent-config:rollback", {
        vendor,
        version_id: versionId,
        expected_generation: String(expectedGeneration),
    });
    return {
        version: normalizeManagedConfigVersion(raw?.version),
        rejections: raw?.rejected ? normalizeManagedConfigRejections(raw.rejected) : [],
    };
}

export async function setManagedAgentConfigEnabled(vendor, enabled) {
    const raw = await restPost("/v1/managed-agent-config:set-enabled", {
        vendor,
        enabled: Boolean(enabled),
    });
    return { enabled: raw?.enabled ?? false };
}

export async function unmanageManagedAgentConfig(vendor, { expectedGeneration, saveNonce }) {
    const raw = await restPost("/v1/managed-agent-config:unmanage", {
        vendor,
        expected_generation: String(expectedGeneration),
        save_nonce: saveNonce,
    });
    return {
        version: normalizeManagedConfigVersion(raw?.version),
        idempotentReplay: first(raw, "idempotentReplay", "idempotent_replay") ?? false,
    };
}

// The two meanings that name the coding agent take its label, so one table
// serves every agent definition.
const FLEET_CATEGORIES = Object.freeze([
    {
        id: "effective",
        wire: "MANAGED_AGENT_CONFIG_FLEET_CATEGORY_EFFECTIVE",
        countCamel: "effective",
        countSnake: "effective",
        label: "Effective",
        tone: "good",
        meaning: "The current policy is applied and present on disk.",
        action: "No action needed.",
    },
    {
        id: "drifted",
        wire: "MANAGED_AGENT_CONFIG_FLEET_CATEGORY_DRIFTED",
        countCamel: "drifted",
        countSnake: "drifted",
        label: "Drifted",
        tone: "warning",
        meaning: "The policy was applied, then its managed values changed.",
        action: "Find the process changing managed-settings.json, then apply a new generation if the tenant policy should win.",
    },
    {
        id: "shadowed",
        wire: "MANAGED_AGENT_CONFIG_FLEET_CATEGORY_SHADOWED",
        countCamel: "shadowed",
        countSnake: "shadowed",
        label: "Shadowed",
        tone: "warning",
        meaning: (agent) => `${agent} reads a higher-priority managed policy source.`,
        action: "Remove the higher-priority source or accept that this tenant policy is not in force there.",
    },
    {
        id: "apply-failed",
        wire: "MANAGED_AGENT_CONFIG_FLEET_CATEGORY_APPLY_FAILED",
        countCamel: "applyFailed",
        countSnake: "apply_failed",
        label: "Apply failed",
        tone: "danger",
        meaning: "The endpoint could not apply the current policy.",
        action: "Review the reported error and repair the endpoint before applying again.",
    },
    {
        id: "stale-generation",
        wire: "MANAGED_AGENT_CONFIG_FLEET_CATEGORY_STALE_GENERATION",
        countCamel: "staleGeneration",
        countSnake: "stale_generation",
        label: "Updating",
        tone: "neutral",
        meaning: "The endpoint has not applied the current policy generation.",
        action: "Wait for its next heartbeat; investigate if it remains behind.",
    },
    {
        id: "no-agent-support",
        wire: "MANAGED_AGENT_CONFIG_FLEET_CATEGORY_NO_AGENT_SUPPORT",
        countCamel: "noAgentSupport",
        countSnake: "no_agent_support",
        label: "Unsupported",
        tone: "neutral",
        meaning: (agent) => `The Origin Agent does not report managed-policy support for ${agent}.`,
        action: "Update the Origin Agent.",
    },
    {
        id: "status-unavailable",
        wire: "MANAGED_AGENT_CONFIG_FLEET_CATEGORY_STATUS_UNAVAILABLE",
        countCamel: "statusUnavailable",
        countSnake: "status_unavailable",
        label: "Status unavailable",
        tone: "danger",
        meaning: "The Origin Agent supports this policy but could not read its durable or on-disk state.",
        action: "Repair the endpoint storage or filesystem error.",
    },
]);

const FLEET_CATEGORY_BY_ID = new Map(FLEET_CATEGORIES.map((category) => [category.id, category]));
const FLEET_CATEGORY_BY_WIRE = new Map(FLEET_CATEGORIES.map((category) => [category.wire, category]));

/// Label and tone for one fleet category id, for a row that carries only the id.
export function managedConfigFleetCategory(id) {
    const category = FLEET_CATEGORY_BY_ID.get(id);
    return category ? { id: category.id, label: category.label, tone: category.tone } : null;
}

function numberField(raw, camel, snake = camel) {
    const value = first(raw, camel, snake);
    const number = Number(value ?? 0);
    return Number.isFinite(number) ? number : 0;
}

export function normalizeManagedConfigFleetSummary(raw, agentLabel = DEFAULT_AGENT_LABEL) {
    const counts = raw?.counts ?? {};
    return {
        desiredGeneration: String(first(raw, "desiredGeneration", "desired_generation") ?? "0"),
        deliveryEnabled: first(raw, "deliveryEnabled", "delivery_enabled") ?? false,
        observedAt: first(raw, "observedAt", "observed_at") ?? null,
        windowStart: first(raw, "windowStart", "window_start") ?? null,
        total: numberField(raw, "reportingEndpoints", "reporting_endpoints"),
        categories: FLEET_CATEGORIES.map((definition) => ({
            ...definition,
            meaning: typeof definition.meaning === "function" ? definition.meaning(agentLabel) : definition.meaning,
            count: numberField(counts, definition.countCamel, definition.countSnake),
        })),
    };
}

export async function getManagedConfigFleetSummary(vendor, { agentLabel } = {}) {
    const raw = await restPost("/v1/managed-agent-config:fleet-summary", {
        vendor,
    });
    return normalizeManagedConfigFleetSummary(raw, agentLabel);
}

function normalizeEndpointState(raw) {
    const category = FLEET_CATEGORY_BY_WIRE.get(raw?.category) ?? FLEET_CATEGORIES[0];
    return {
        // Sent back verbatim in proto `bytes` fields (cursor, drift lookup), so
        // it stays base64; `endpointUuid` is the hyphenated display form.
        endpointId: first(raw, "endpointId", "endpoint_id") ?? "",
        endpointUuid: endpointIdToUuid(first(raw, "endpointId", "endpoint_id") ?? ""),
        hostname: raw?.hostname ?? "",
        category: category.id,
        lastHeartbeatAt: first(raw, "lastHeartbeatAt", "last_heartbeat_at") ?? null,
        appliedGeneration: String(first(raw, "appliedGeneration", "applied_generation") ?? "0"),
        attemptedGeneration: String(first(raw, "attemptedGeneration", "attempted_generation") ?? "0"),
        attemptCount: numberField(raw, "attemptCount", "attempt_count"),
        presenceState: first(raw, "presenceState", "presence_state") ?? "MANAGED_AGENT_CONFIG_PRESENCE_STATE_UNSPECIFIED",
        shadowTier: first(raw, "shadowTier", "shadow_tier") ?? "MANAGED_AGENT_CONFIG_SHADOW_TIER_UNSPECIFIED",
        lastOutcome: first(raw, "lastOutcome", "last_outcome") ?? "MANAGED_CONFIG_APPLY_OUTCOME_UNSPECIFIED",
        lastErrorCode: first(raw, "lastErrorCode", "last_error_code") ?? "MANAGED_CONFIG_APPLY_ERROR_CODE_UNSPECIFIED",
    };
}

const APPLY_ERROR_COPY = Object.freeze({
    MANAGED_CONFIG_APPLY_ERROR_CODE_HASH_MISMATCH: "The delivered document did not match its digest.",
    MANAGED_CONFIG_APPLY_ERROR_CODE_EMPTY_DOCUMENT: "The delivered document was empty.",
    MANAGED_CONFIG_APPLY_ERROR_CODE_VALIDATION_REJECTED: "The endpoint validator rejected the document.",
    MANAGED_CONFIG_APPLY_ERROR_CODE_ORIGIN_OWNERSHIP_COLLISION: "A tenant setting collided with an Origin-owned value.",
    MANAGED_CONFIG_APPLY_ERROR_CODE_FILESYSTEM: "The managed settings file could not be updated.",
    MANAGED_CONFIG_APPLY_ERROR_CODE_STORAGE: "The Origin Agent could not update its durable policy state.",
    MANAGED_CONFIG_APPLY_ERROR_CODE_PANIC_QUARANTINED: "The apply was quarantined after an internal failure.",
    MANAGED_CONFIG_APPLY_ERROR_CODE_STATE_READ: "The Origin Agent could not read its policy state.",
});

const SHADOW_TIER_COPY = Object.freeze({
    MANAGED_AGENT_CONFIG_SHADOW_TIER_MDM_ADMIN: "An MDM administrator policy has higher priority.",
    MANAGED_AGENT_CONFIG_SHADOW_TIER_REMOTE_MANAGED: "A remote managed policy has higher priority.",
    MANAGED_AGENT_CONFIG_SHADOW_TIER_POLICY_HELPER: "A policy-helper source has higher priority.",
});

export function managedConfigEndpointReason(endpoint, desiredGeneration = "0", agentLabel = DEFAULT_AGENT_LABEL) {
    switch (endpoint.category) {
    case "effective": return "The current policy is present on disk.";
    case "drifted": return "Managed values differ from the generation Origin applied.";
    case "shadowed": return SHADOW_TIER_COPY[endpoint.shadowTier] ?? "A higher-priority policy source is in force.";
    case "apply-failed": {
        const reason = APPLY_ERROR_COPY[endpoint.lastErrorCode] ?? "The endpoint reported an unspecified apply failure.";
        const attempts = endpoint.attemptCount === 1 ? "1 attempt" : `${endpoint.attemptCount} attempts`;
        return `${reason} Generation ${endpoint.attemptedGeneration}, ${attempts}.`;
    }
    case "stale-generation": return `Applied generation ${endpoint.appliedGeneration}; waiting for generation ${desiredGeneration}.`;
    case "no-agent-support": return `This Origin Agent does not report ${agentLabel} managed-policy support.`;
    case "status-unavailable": return APPLY_ERROR_COPY[endpoint.lastErrorCode] ?? "The Origin Agent could not read its policy state.";
    default: return "The endpoint reported an unrecognized managed-policy state.";
    }
}

const SHADOW_TIER_LAYERS = Object.freeze({
    MANAGED_AGENT_CONFIG_SHADOW_TIER_REMOTE_MANAGED: ["SETTINGS_LAYER_REMOTE_MANAGED"],
    MANAGED_AGENT_CONFIG_SHADOW_TIER_MDM_ADMIN: ["SETTINGS_LAYER_MDM_ADMIN"],
    MANAGED_AGENT_CONFIG_SHADOW_TIER_POLICY_HELPER: ["SETTINGS_LAYER_REMOTE_MANAGED", "SETTINGS_LAYER_MDM_ADMIN"],
});

/// Source paths of the config files behind a shadowed endpoint's tier, from its
/// current config state. Mirrors the Origin Agent's test: the file exists and
/// holds a non-empty top-level object; for a policy helper, one that declares
/// `policyHelper`. A body that cannot be parsed is kept, since it may be the source.
export function shadowingSourcePaths(shadowTier, currentTargets, provider) {
    const layers = SHADOW_TIER_LAYERS[shadowTier];
    if (!layers) return [];
    const paths = (currentTargets ?? [])
        .filter((target) => target.exists && target.agentProvider === provider && layers.includes(target.layer))
        .filter((target) => {
            let body;
            try { body = JSON.parse(target.configContentJson); }
            catch { return true; }
            if (!body || typeof body !== "object" || Array.isArray(body) || Object.keys(body).length === 0) return false;
            return shadowTier !== "MANAGED_AGENT_CONFIG_SHADOW_TIER_POLICY_HELPER" || "policyHelper" in body;
        })
        .map((target) => target.sourcePath);
    return [...new Set(paths)].sort();
}

export function managedConfigEndpointGeneration(endpoint) {
    if (endpoint.category === "no-agent-support") return "Not supported";
    if (endpoint.attemptedGeneration !== "0" && endpoint.attemptedGeneration !== endpoint.appliedGeneration) {
        return `${endpoint.appliedGeneration} applied / ${endpoint.attemptedGeneration} attempted`;
    }
    return endpoint.appliedGeneration;
}

export async function listManagedConfigEndpointReasons(vendor, { category, cursor = "", observedAt = null } = {}) {
    const definition = FLEET_CATEGORY_BY_ID.get(category);
    const raw = await restPost("/v1/managed-agent-config:endpoint-states", {
        vendor,
        ...(definition ? { category: definition.wire } : {}),
        ...(cursor ? { after_endpoint_id: cursor } : {}),
        ...(observedAt ? { observed_at: observedAt } : {}),
    });
    return {
        endpoints: (raw?.states ?? []).map(normalizeEndpointState),
        nextCursor: first(raw, "nextAfterEndpointId", "next_after_endpoint_id") ?? "",
        observedAt: first(raw, "observedAt", "observed_at") ?? observedAt,
    };
}

export function normalizeManagedConfigPreview(raw) {
    const reachable = raw?.reachable;
    const rejected = raw?.rejected;
    return {
        total: numberField(reachable, "reportingEndpoints", "reporting_endpoints"),
        reachable: numberField(reachable, "reachableEndpoints", "reachable_endpoints"),
        shadowed: numberField(reachable, "excludedShadowed", "excluded_shadowed"),
        unsupported: numberField(reachable, "excludedNoAgentSupport", "excluded_no_agent_support"),
        observedAt: first(reachable, "observedAt", "observed_at") ?? null,
        rejections: rejected ? normalizeManagedConfigRejections(rejected) : [],
    };
}

export async function previewManagedConfigReachability(vendor, document) {
    const raw = await restPost("/v1/managed-agent-config:preview", {
        vendor,
        document,
    });
    return normalizeManagedConfigPreview(raw);
}

function normalizeCatalogEntry(raw) {
    return {
        pointer: raw?.pointer ?? "",
        description: raw?.description ?? "",
        valueKind: first(raw, "valueKind", "value_kind") ?? "MANAGED_AGENT_CONFIG_VALUE_KIND_UNSPECIFIED",
        minimum: raw?.minimum ?? null,
        allowedValues: first(raw, "allowedValues", "allowed_values") ?? [],
        elementRule: first(raw, "elementRule", "element_rule") ?? "MANAGED_AGENT_CONFIG_STRING_RULE_UNSPECIFIED",
        objectShapes: first(raw, "objectShapes", "object_shapes") ?? [],
        suggestedValues: first(raw, "suggestedValues", "suggested_values") ?? [],
    };
}

export function normalizeManagedConfigCatalog(raw) {
    const settings = (raw?.entries ?? []).map(normalizeCatalogEntry);
    const at = (pointer) => settings.find((setting) => setting.pointer === pointer);
    return {
        settings,
        defaultModes: (at("/permissions/defaultMode")?.allowedValues ?? []).map((value) => ({ value, label: value })),
        customizationSurfaces: at("/strictPluginOnlyCustomization")?.allowedValues ?? [],
        loginMethods: (at("/forceLoginMethod")?.allowedValues ?? []).map((value) => ({ value, label: value })),
    };
}

export async function getManagedConfigControlCatalog(vendor) {
    const raw = await restPost("/v1/managed-agent-config:catalog", {
        vendor,
    });
    return normalizeManagedConfigCatalog(raw);
}

export async function getManagedAgentConfigVersion(vendor, versionId) {
    const raw = await restPost("/v1/managed-agent-config:get-version", {
        vendor,
        version_id: versionId,
    });
    return normalizeManagedConfigVersion(raw?.version);
}

function normalizeReleaseEndpoint(raw) {
    return {
        endpointId: first(raw, "endpointId", "endpoint_id") ?? "",
        addedBy: first(raw, "addedBy", "added_by") ?? "",
        addedByEmail: first(raw, "addedByEmail", "added_by_email") ?? "",
        addedAt: first(raw, "addedAt", "added_at") ?? null,
        confirmedBy: first(raw, "confirmedBy", "confirmed_by") ?? "",
        confirmedByEmail: first(raw, "confirmedByEmail", "confirmed_by_email") ?? "",
        confirmedAt: first(raw, "confirmedAt", "confirmed_at") ?? null,
        excludedBy: first(raw, "excludedBy", "excluded_by") ?? "",
        excludedByEmail: first(raw, "excludedByEmail", "excluded_by_email") ?? "",
        excludedAt: first(raw, "excludedAt", "excluded_at") ?? null,
        exclusionReason: first(raw, "exclusionReason", "exclusion_reason") ?? "",
    };
}

// A release endpoint is confirmed or excluded by its timestamps: the server
// clears the pair when the action is undone.
export function releaseEndpointConfirmed(endpoint) {
    return Boolean(endpoint?.confirmedAt);
}

export function releaseEndpointExcluded(endpoint) {
    return Boolean(endpoint?.excludedAt);
}

export function normalizeManagedConfigRelease(raw, fallbackLimit = 0) {
    if (!raw) return null;
    return {
        releaseId: first(raw, "releaseId", "release_id") ?? "",
        state: normalizeReleaseState(raw.state),
        version: normalizeManagedConfigVersion(raw.version),
        baseGeneration: String(first(raw, "baseGeneration", "base_generation") ?? "0"),
        signedBy: first(raw, "signedBy", "signed_by") ?? "",
        signedByEmail: first(raw, "signedByEmail", "signed_by_email") ?? "",
        signedAt: first(raw, "signedAt", "signed_at") ?? null,
        abandonedBy: first(raw, "abandonedBy", "abandoned_by") ?? "",
        abandonedByEmail: first(raw, "abandonedByEmail", "abandoned_by_email") ?? "",
        abandonedAt: first(raw, "abandonedAt", "abandoned_at") ?? null,
        endpoints: (raw.endpoints ?? []).map(normalizeReleaseEndpoint),
        pilotEndpointLimit: numberField(raw, "pilotEndpointLimit", "pilot_endpoint_limit") || fallbackLimit,
    };
}

export async function startManagedAgentConfigPilot(vendor, { document, expectedGeneration, saveNonce, endpointIds }) {
    const raw = await restPost("/v1/managed-agent-config:start-pilot", {
        vendor,
        document,
        expected_generation: String(expectedGeneration),
        save_nonce: saveNonce,
        endpoint_ids: [...endpointIds],
    });
    return {
        release: normalizeManagedConfigRelease(raw?.release),
        rejections: raw?.rejected ? normalizeManagedConfigRejections(raw.rejected) : [],
        idempotentReplay: first(raw, "idempotentReplay", "idempotent_replay") ?? false,
    };
}

// Without `releaseId` this reads the open release, which is absent when no
// pilot is running.
export async function getManagedAgentConfigRelease(vendor, { releaseId = "" } = {}) {
    const raw = await restPost("/v1/managed-agent-config:get-release", {
        vendor,
        ...(releaseId ? { release_id: releaseId } : {}),
    });
    const pilotEndpointLimit = numberField(raw, "pilotEndpointLimit", "pilot_endpoint_limit");
    return {
        release: normalizeManagedConfigRelease(raw?.release, pilotEndpointLimit),
        pilotEndpointLimit,
    };
}

export const RELEASE_ENDPOINT_ACTION = Object.freeze({
    ADD: "MANAGED_AGENT_CONFIG_RELEASE_ENDPOINT_ACTION_ADD",
    CONFIRM: "MANAGED_AGENT_CONFIG_RELEASE_ENDPOINT_ACTION_CONFIRM",
    UNCONFIRM: "MANAGED_AGENT_CONFIG_RELEASE_ENDPOINT_ACTION_UNCONFIRM",
    EXCLUDE: "MANAGED_AGENT_CONFIG_RELEASE_ENDPOINT_ACTION_EXCLUDE",
    RESTORE: "MANAGED_AGENT_CONFIG_RELEASE_ENDPOINT_ACTION_RESTORE",
});

export async function updateManagedAgentConfigReleaseEndpoint(vendor, { releaseId, endpointId, action, reason = "" }) {
    const raw = await restPost("/v1/managed-agent-config:update-release-endpoint", {
        vendor,
        release_id: releaseId,
        endpoint_id: endpointId,
        action,
        ...(action === RELEASE_ENDPOINT_ACTION.EXCLUDE ? { reason } : {}),
    });
    return { release: normalizeManagedConfigRelease(raw?.release) };
}

function normalizePilotEndpointState(raw) {
    const reported = raw?.reported === true;
    const category = reported ? FLEET_CATEGORY_BY_WIRE.get(raw?.category) : null;
    return {
        endpointId: first(raw, "endpointId", "endpoint_id") ?? "",
        hostname: raw?.hostname ?? "",
        os: raw?.os ?? "",
        agentVersion: first(raw, "agentVersion", "agent_version") ?? "",
        reported,
        category: category?.id ?? "",
        lastHeartbeatAt: first(raw, "lastHeartbeatAt", "last_heartbeat_at") ?? null,
        appliedGeneration: String(first(raw, "appliedGeneration", "applied_generation") ?? "0"),
        lastErrorCode: first(raw, "lastErrorCode", "last_error_code") ?? "MANAGED_CONFIG_APPLY_ERROR_CODE_UNSPECIFIED",
        appliedAt: first(raw, "appliedAt", "applied_at") ?? null,
        heartbeatsSinceApply: numberField(raw, "heartbeatsSinceApply", "heartbeats_since_apply"),
        agentEventsSinceApply: numberField(raw, "agentEventsSinceApply", "agent_events_since_apply"),
    };
}

export async function listManagedAgentConfigPilotEndpointStates(vendor, releaseId) {
    const raw = await restPost("/v1/managed-agent-config:pilot-endpoint-states", {
        vendor,
        release_id: releaseId,
    });
    return {
        states: (raw?.states ?? []).map(normalizePilotEndpointState),
        observedAt: first(raw, "observedAt", "observed_at") ?? null,
    };
}

export async function signManagedAgentConfigRelease(vendor, { releaseId, documentSha256, expectedGeneration, attested }) {
    const raw = await restPost("/v1/managed-agent-config:sign-release", {
        vendor,
        release_id: releaseId,
        document_sha256: documentSha256,
        expected_generation: String(expectedGeneration),
        attested: attested === true,
    });
    return { release: normalizeManagedConfigRelease(raw?.release) };
}

export async function abandonManagedAgentConfigRelease(vendor, { releaseId, saveNonce }) {
    const raw = await restPost("/v1/managed-agent-config:abandon-release", {
        vendor,
        release_id: releaseId,
        save_nonce: saveNonce,
    });
    return {
        release: normalizeManagedConfigRelease(raw?.release),
        version: normalizeManagedConfigVersion(raw?.version),
        idempotentReplay: first(raw, "idempotentReplay", "idempotent_replay") ?? false,
    };
}

export function createSaveNonce(random = globalThis.crypto) {
    if (!random?.getRandomValues || typeof globalThis.btoa !== "function") {
        throw new Error("Secure browser randomness is unavailable");
    }
    const bytes = new Uint8Array(16);
    random.getRandomValues(bytes);
    let binary = "";
    for (const byte of bytes) binary += String.fromCharCode(byte);
    return globalThis.btoa(binary);
}
